const AUDIO_FORMATS = new Set(['WAV', 'AIFF', 'AIFC', 'FLAC', 'MP3', 'M4A', 'AAC', 'OGG', 'OPUS', 'ALAC', 'WMA', 'APE', 'AMR']);

export function audioFormat(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const aliases: Record<string, string> = { AIF: 'AIFF', WAVE: 'WAV', 'AUDIO/WAV': 'WAV', 'AUDIO/X-WAV': 'WAV', 'AUDIO/AIFF': 'AIFF', 'AUDIO/X-AIFF': 'AIFF', 'AUDIO/MPEG': 'MP3', 'AUDIO/MP4': 'M4A' };
  const raw = value.trim().replace(/^\./, '').toUpperCase();
  const normalized = aliases[raw] || raw;
  return AUDIO_FORMATS.has(normalized) ? normalized : null;
}

export function audioFormatFromUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    const path = decodeURIComponent(new URL(value, 'https://local.invalid').pathname);
    return audioFormat(path.match(/\.([a-z0-9]+)$/i)?.[1]);
  } catch { return null; }
}

/** Count distinct stored format variants per track, including master copies. */
function audioAssets(work: Record<string, unknown>) {
  const assets = new Map<string, string>();
  const add = (url: unknown, metadata?: unknown) => {
    if (typeof url !== 'string' || !url.trim()) return;
    const extension = (() => { try { return decodeURIComponent(new URL(url, 'https://local.invalid').pathname).match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase(); } catch { return undefined; } })();
    if (extension && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'mp4', 'mov', 'webm', 'pdf', 'zip'].includes(extension)) return;
    const format = audioFormatFromUrl(url) || audioFormat(metadata) || 'Unknown';
    if (!assets.has(url) || assets.get(url) === 'Unknown') assets.set(url, format);
  };
  add(work.audioUrl, work.fileFormat || work.format || work.contentType);
  add(work.mp3Url, work.fileFormat || work.format);
  add(work.sourceUrl, work.sourceFormat);
  add(work.fileUrl, work.fileFormat || work.format);
  add(work.hiResUrl, work.hiResFileFormat);
  add(work.wavUrl, 'WAV');
  add(work.sourceUrlOriginal, work.originalFileFormat);
  add(work.originalAudioUrl, work.originalFileFormat);
  add(work.originalFileUrl, work.originalFileFormat);
  return assets;
}

export function storedAudioFormats(work: Record<string, unknown>): string[] {
  return [...new Set(audioAssets(work).values())].filter(format => format !== 'Unknown');
}

export function unresolvedAudioAssetCount(work: Record<string, unknown>): number {
  return [...audioAssets(work).values()].filter(format => format === 'Unknown').length;
}

export function isAudioWork(work: Record<string, unknown>): boolean {
  if (audioAssets(work).size) return true;
  return ['work', 'song', 'track', 'single', 'beat', 'demo', 'instrumental', 'dj mix', 'podcast episode'].includes(String(work.type || '').toLowerCase());
}

export function catalogueDate(value: unknown): Date | null {
  if (value == null) return null;
  let date: Date;
  if (typeof (value as { toDate?: unknown }).toDate === 'function') date = (value as { toDate(): Date }).toDate();
  else if (typeof value === 'object' && typeof ((value as { seconds?: unknown; _seconds?: unknown }).seconds ?? (value as { _seconds?: unknown })._seconds) === 'number') date = new Date(Number((value as { seconds?: number; _seconds?: number }).seconds ?? (value as { _seconds?: number })._seconds) * 1000);
  else if (typeof value === 'number') date = new Date(value < 1e11 ? value * 1000 : value);
  else if (typeof value === 'string') date = new Date(value);
  else return null;
  return Number.isFinite(date.getTime()) ? date : null;
}

export function trackDurationMs(work: Record<string, unknown>): number | null {
  for (const value of [Number(work.durationMs), Number(work.durationSeconds) * 1000]) {
    if (Number.isFinite(value) && value > 0) return value;
  }
  if (typeof work.duration === 'string') {
    const match = work.duration.trim().match(/^(?:(\d+):)?(\d+):([0-5]?\d)$/);
    if (match && (!match[1] || Number(match[2]) < 60)) {
      const value = (Number(match[1] || 0) * 3600 + Number(match[2]) * 60 + Number(match[3])) * 1000;
      if (value > 0) return value;
    }
  }
  return null;
}

export function monthBuckets(now = new Date(), length = 6) {
  return Array.from({ length }, (_, i) => {
    const start = new Date(now.getFullYear(), now.getMonth() - length + i + 1, 1);
    return { key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`, month: start.toLocaleString('en', { month: 'short', year: '2-digit' }), count: 0 };
  });
}

export function canViewStreams(accountType: unknown): boolean {
  return ['creator', 'admin', 'superadmin'].includes(String(accountType || '').toLowerCase());
}
